import React from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  useTheme,
  alpha,
  CircularProgress,
  Stack,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

/**
 * Empty state reutilizável com explicação do objetivo da tela e botão "Cadastrar com IA".
 * UX/UI rica: ícone, diagramação, CTA primário (IA) e secundário (manual).
 *
 * @param {string} title - Título do empty state (ex: "Nenhum nível de consciência cadastrado")
 * @param {string} description - Texto explicando o objetivo do item/tela
 * @param {string} primaryLabel - Label do botão principal (ex: "Criar com sugestões de IA")
 * @param {string} [secondaryLabel] - Label do botão secundário (ex: "Adicionar manualmente")
 * @param {function} onPrimaryClick - Handler do botão principal (ex: gerar com IA)
 * @param {function} [onSecondaryClick] - Handler do botão secundário (ex: abrir formulário manual)
 * @param {boolean} [primaryLoading] - Loading do botão principal
 * @param {React.ReactNode} [icon] - Ícone opcional (default: AutoAwesome)
 * @param {string} [maxWidth] - Largura máxima do Paper (ex: "560px")
 * @param {{ label: string, onClick: function, icon?: React.ReactNode, disabled?: boolean, loading?: boolean }[]} [extraActions] - Botões adicionais
 */
const EmptyStateWithAI = ({
  title,
  description,
  primaryLabel,
  secondaryLabel,
  onPrimaryClick,
  onSecondaryClick,
  primaryLoading = false,
  icon,
  maxWidth = "600px",
  extraActions = [],
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const IconComponent = icon ?? (
    <AutoAwesomeIcon sx={{ fontSize: 56 }} color="primary" />
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        maxWidth,
        mx: "auto",
        overflow: "hidden",
        borderRadius: 3,
        border: `1px solid ${alpha(
          theme.palette.primary.main,
          isDark ? 0.3 : 0.2
        )}`,
        background: isDark
          ? `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.primary.dark, 0.08)} 100%)`
          : `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
        boxShadow: isDark
          ? `0 8px 32px ${alpha(theme.palette.common.black, 0.4)}`
          : `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
      }}
    >
      <Box sx={{ p: { xs: 3, sm: 4 } }}>
        {/* Área visual: ícone + destaque */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mb: 3,
            py: 2,
            px: 2,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, isDark ? 0.12 : 0.08),
          }}
        >
          {IconComponent}
        </Box>

        <Typography
          variant="h6"
          component="h2"
          gutterBottom
          textAlign="center"
          sx={{
            fontWeight: 600,
            color: theme.palette.primary.main,
            fontSize: { xs: "1.1rem", sm: "1.25rem" },
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            textAlign: "left",
            mb: 3,
            lineHeight: 1.7,
            px: { xs: 0, sm: 1 },
          }}
        >
          {description}
        </Typography>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="center"
          justifyContent="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Button
            variant="contained"
            size="large"
            startIcon={
              primaryLoading ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                <AutoAwesomeIcon />
              )
            }
            disabled={primaryLoading}
            onClick={onPrimaryClick}
            sx={{
              px: 3,
              py: 1.5,
              fontWeight: 600,
              boxShadow: 2,
              "&:hover": {
                boxShadow: 4,
              },
            }}
          >
            {primaryLoading ? "Gerando sugestões..." : primaryLabel}
          </Button>
          {onSecondaryClick && (
            <Button
              variant="outlined"
              size="large"
              startIcon={<AddCircleOutlineIcon />}
              onClick={onSecondaryClick}
              sx={{
                px: 3,
                py: 1.5,
                fontWeight: 500,
              }}
            >
              {secondaryLabel ?? "Adicionar manualmente"}
            </Button>
          )}
          {extraActions.map((action, idx) => (
            <Button
              key={action.label || idx}
              variant="outlined"
              size="large"
              startIcon={
                action.loading ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  action.icon
                )
              }
              disabled={action.disabled || action.loading || primaryLoading}
              onClick={action.onClick}
              sx={{
                px: 3,
                py: 1.5,
                fontWeight: 500,
              }}
            >
              {action.label}
            </Button>
          ))}
        </Stack>
      </Box>
    </Paper>
  );
};

export default EmptyStateWithAI;
